import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import i18n, { languages, LanguageCode } from "@/i18n/config";

interface LanguageContextType {
  currentLanguage: LanguageCode;
  setLanguage: (code: LanguageCode) => Promise<void>;
  languages: typeof languages;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "i18nextLng";
const SUPPORTED_LANGUAGES: LanguageCode[] = ["en", "es", "fr", "it", "de"];

function getValidLanguageCode(code: string | undefined | null): LanguageCode {
  if (!code) return "en";
  // Handle codes like "en-US" -> "en"
  const baseCode = code.split("-")[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(baseCode as LanguageCode) 
    ? (baseCode as LanguageCode) 
    : "en";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(
    getValidLanguageCode(i18n.language)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Load language preference based on priority:
  // 1. Supabase user metadata (if authenticated)
  // 2. localStorage
  // 3. Browser language
  // 4. Default to English
  const loadLanguagePreference = useCallback(async (user: User | null) => {
    setIsLoading(true);
    
    try {
      let preferredLanguage: LanguageCode = "en";

      if (user) {
        // Check Supabase user metadata first
        const userLanguage = user.user_metadata?.language;
        if (userLanguage && SUPPORTED_LANGUAGES.includes(userLanguage)) {
          preferredLanguage = userLanguage as LanguageCode;
        } else {
          // If no user metadata, check localStorage
          const storedLanguage = localStorage.getItem(STORAGE_KEY);
          if (storedLanguage) {
            preferredLanguage = getValidLanguageCode(storedLanguage);
            // Save to user metadata if not already set
            await supabase.auth.updateUser({
              data: { language: preferredLanguage }
            });
          } else {
            // Use browser language
            preferredLanguage = getValidLanguageCode(navigator.language);
            // Save to user metadata
            await supabase.auth.updateUser({
              data: { language: preferredLanguage }
            });
          }
        }
      } else {
        // Not authenticated - check localStorage then browser
        const storedLanguage = localStorage.getItem(STORAGE_KEY);
        if (storedLanguage) {
          preferredLanguage = getValidLanguageCode(storedLanguage);
        } else {
          preferredLanguage = getValidLanguageCode(navigator.language);
        }
      }

      // Apply the language
      if (i18n.language !== preferredLanguage) {
        await i18n.changeLanguage(preferredLanguage);
      }
      setCurrentLanguage(preferredLanguage);
      localStorage.setItem(STORAGE_KEY, preferredLanguage);
    } catch (error) {
      console.error("Error loading language preference:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const user = session?.user ?? null;
        setCurrentUser(user);
        
        // Defer the language loading to avoid Supabase deadlock
        if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
          setTimeout(() => {
            loadLanguagePreference(user);
          }, 0);
        } else if (event === "SIGNED_OUT") {
          // Keep current localStorage language when signing out
          setIsLoading(false);
        }
      }
    );

    // Initial check
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user ?? null;
      setCurrentUser(user);
      loadLanguagePreference(user);
    });

    return () => subscription.unsubscribe();
  }, [loadLanguagePreference]);

  // Keep i18n language in sync
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(getValidLanguageCode(lng));
    };

    i18n.on("languageChanged", handleLanguageChanged);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  const setLanguage = useCallback(async (code: LanguageCode) => {
    try {
      // Update i18n
      await i18n.changeLanguage(code);
      setCurrentLanguage(code);
      
      // Persist to localStorage
      localStorage.setItem(STORAGE_KEY, code);

      // If authenticated, also save to Supabase user metadata
      if (currentUser) {
        await supabase.auth.updateUser({
          data: { language: code }
        });
      }
    } catch (error) {
      console.error("Error setting language:", error);
    }
  }, [currentUser]);

  return (
    <LanguageContext.Provider 
      value={{ 
        currentLanguage, 
        setLanguage, 
        languages,
        isLoading 
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
